/**
 * Le bac à sable : lancer les dés pour le plaisir, sans partie autour.
 *
 * C'était l'écran du lot 3, et il reste utile — c'est là qu'on juge le jet, la
 * matière des dés et le cadrage, sans qu'une règle vienne s'interposer. Il garde donc
 * son propre état de tapis (`scene/tapis.ts`, 16 tests) plutôt que d'emprunter celui
 * du moteur.
 *
 * Une différence de fond avec la partie : **ici, c'est cet écran qui tire**. Le
 * tirage passe par le générateur du moteur partagé, dont l'équité est vérifiée sur
 * 600 000 dés — pas par la physique, qui ne fait jamais que mettre en scène.
 */

import { evaluer, tireurCrypto } from "@zinc421/shared";
import { useCallback, useMemo, useState } from "react";

import { useTextes } from "../i18n/contexte.js";
import { nomDeCombinaison } from "../jeu/libelles.js";
import { Scene } from "../scene/Scene.js";
import type { DemandeDeJet } from "../scene/DesQuiRoulent.js";
import type { Jet } from "../scene/lancer.js";
import {
  appliquerJet, basculerGarde, indicesARelancer, mainDuTapis, relanceUtile,
  tapisInitial, toutRelacher,
} from "../scene/tapis.js";

export function BacASable({ onQuitter }: { readonly onQuitter: () => void }) {
  const textes = useTextes();
  const [tapis, setTapis] = useState(tapisInitial);
  const [demande, setDemande] = useState<DemandeDeJet | null>(null);
  /** Incrémenté à chaque lancer : c'est l'identité du jet pour la scène. */
  const [jets, setJets] = useState(0);

  const lancer = useCallback(() => {
    const indices = indicesARelancer(tapis);
    if (indices.length === 0) return;
    setJets((n) => n + 1);
    setDemande({
      id: jets + 1,
      indices,
      // Le résultat vient du tireur, jamais de la physique.
      faces: tireurCrypto(indices.length),
      graine: Math.floor(Math.random() * 0x7fffffff),
    });
  }, [tapis, jets]);

  const surJetJoue = useCallback((indices: readonly number[], jet: Jet) => {
    setTapis((precedent) => appliquerJet(precedent, indices, jet));
    setDemande(null);
  }, []);

  const surDeClique = useCallback((index: number) => {
    // Pendant un jet, on ne réarrange pas les dés. Via la demande courante, et non en
    // lisant l'état dans un updater : un updater doit être pur, et React le rappelle
    // volontairement deux fois en mode strict — la garde basculait alors deux fois et
    // le clic paraissait ignoré.
    if (demande !== null) return;
    setTapis((precedent) => basculerGarde(precedent, index));
  }, [demande]);

  const main = mainDuTapis(tapis);
  const combinaison = useMemo(
    () => (main ? evaluer(main[0], main[1], main[2]) : null), [main]);
  const gardes = tapis.filter((d) => d.garde).length;
  const aRelancer = indicesARelancer(tapis).length;
  const des = tapis.map((d) => ({ valeur: d.face, garde: d.garde }));
  const poses = tapis.map((d) => d.pose);

  return (
    <div className="ecran">
      <Scene
        des={des}
        poses={poses}
        demande={demande}
        onJetJoue={surJetJoue}
        onDeClique={surDeClique}
        onContextePerdu={() => setDemande(null)}
      />

      <div className="hud">
        <h1>{textes.appNom}</h1>
        <p className="etape">
          {jets === 0
            ? textes.bacLanceLesDes
            : demande !== null
              ? textes.desQuiRoulent
              : gardes === 3
                ? textes.bacTuGardesTout
                : textes.bacToucheUnDe}
        </p>

        {/* Les dés, en boutons : seule façon de jouer au clavier, et seule façon
            pour un lecteur d'écran de connaître l'état du tapis. */}
        <ul className="des" aria-label={textes.lesTroisDes}>
          {tapis.map((de, i) => (
            <li key={i}>
              <button
                type="button"
                className={`de${de.garde ? " de--garde" : ""}`}
                aria-pressed={de.garde}
                disabled={demande !== null || de.face === null}
                onClick={() => surDeClique(i)}
              >
                <span aria-hidden="true">{de.face ?? "?"}</span>
                <span className="lecteur-ecran">
                  {`Dé ${i + 1} : ${de.face === null ? textes.bacPasEncoreLance
                    : (de.garde ? textes.deGarde(de.face) : String(de.face))}`}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <div className="actions">
          <button
            className="bouton"
            onClick={lancer}
            disabled={demande !== null || !relanceUtile(tapis)}
          >
            {demande !== null
              ? textes.desQuiRoulent
              : jets === 0
                ? textes.lancerLesDes
                : textes.relancerDes(aRelancer)}
          </button>
          {jets > 0 && gardes > 0 && (
            <button
              className="bouton bouton--discret"
              onClick={() => setTapis(toutRelacher)}
              disabled={demande !== null}
            >
              {textes.toutRelacher}
            </button>
          )}
        </div>

        <p className="resultat" role="status" aria-live="polite">
          {combinaison && demande === null
            ? textes.combinaisonEtJetons(nomDeCombinaison(combinaison, textes), textes.jetons(combinaison.jetons))
            : " "}
        </p>
        <p className="note">{textes.bacMoteurNomme}</p>
      </div>

      <button className="quitter" onClick={onQuitter}>{textes.retour}</button>
    </div>
  );
}
